import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { $crud } from "../helper/CrudFactory";
import {
  loadCalendarAccounts,
  getCalendarOAuthUrl,
  connectCalDAVAccount,
  updateCalendarAccount,
  disconnectCalendarAccount,
  listCalendarChoices,
  syncAllCalendarAccounts,
} from "../helper/calendarApi";
import { REFRESH_TODO_LIST } from "../store";
import LanguageContext from "../helper/LanguageContext";
import { SettingsNote, SettingsRow, SettingsTag } from "./settings/SettingsList";
import "./CalendarSyncSettings.css";
import {
  companyTrialEndedAskBody,
  navigateToKjuupIoBilling,
  shouldShowCompanyTrialEndedNote,
} from "../utils/kjuupIoBillingNav";

/**
 * A settings row whose value is picked from a list: a transparent native <select>
 * covers the row, so a click anywhere opens the options (mobile: a picker sheet).
 */
function SelectRow({ icon, label, value, valueLabel, options, onChange, onFocus, disabled }) {
  return (
    <div className={`kj-settings-item cal-pro__select-item${disabled ? " is-disabled" : ""}`}>
      <div className="kj-settings-row">
        <span className="kj-settings-row__tile" aria-hidden="true">
          <span className="material-symbols-outlined">{icon}</span>
        </span>
        <span className="kj-settings-row__text">
          <span className="kj-settings-row__label">{label}</span>
        </span>
        <span className="kj-settings-row__value">{valueLabel}</span>
        <span className="material-symbols-outlined kj-settings-row__chevron" aria-hidden="true">unfold_more</span>
      </div>
      <select
        className="cal-pro__select"
        value={value}
        disabled={disabled}
        onFocus={onFocus}
        onChange={(e) => onChange(e.target.value)}
        aria-label={label}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

/**
 * Calendar sync settings (mobile CalendarSettingsPro): Google / Outlook OAuth and the
 * Apple / CalDAV form, then one card per connected calendar.
 * When `locked` (no active Kjuup.io plan while Stripe UI is enabled), the Kjuup.io
 * offer replaces the options — same pattern as Email Accounts.
 */
export default function CalendarSyncSettings({
  strings = {},
  locked = false,
  hasUsedTrial = false,
}) {
  const { isGerm } = React.useContext(LanguageContext);
  const tr = (key, de, en) => strings[key] || (isGerm ? de : en);
  const history = useHistory();
  const dispatch = useDispatch();
  const subscription = useSelector((s) => s.subscription) || {};
  const isKjuupPlusMember = !!subscription.isKjuupPlusMember;
  const showCompanyTrialEndedNote = shouldShowCompanyTrialEndedNote(subscription);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [connecting, setConnecting] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [syncingNow, setSyncingNow] = useState(false);
  const [calendarsByAccount, setCalendarsByAccount] = useState({});
  const [showCalDav, setShowCalDav] = useState(false);
  const [calDavForm, setCalDavForm] = useState({
    provider: "apple",
    username: "",
    password: "",
    serverUrl: "",
  });
  const oauthTimerRef = useRef(null);
  const calendarsFetchedRef = useRef(new Set());

  const t = (key, fallback) => strings[key] || fallback;

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const list = await loadCalendarAccounts();
      setAccounts(list);
      return list;
    } catch (e) {
      console.error("calendar accounts fetch failed", e);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (locked) return;
    fetchAccounts();
  }, [fetchAccounts, locked]);

  // Prefetch target calendars for Google / Outlook accounts.
  useEffect(() => {
    (accounts || []).forEach((account) => {
      if (account.provider !== "google" && account.provider !== "outlook") return;
      if (calendarsFetchedRef.current.has(account.id)) return;
      calendarsFetchedRef.current.add(account.id);
      listCalendarChoices(account.id).then((list) => {
        if (list?.length) {
          setCalendarsByAccount((prev) => ({ ...prev, [account.id]: list }));
        }
      });
    });
  }, [accounts]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("calendarSync") === "success") {
      $crud.notify({
        type: "success",
        message: t("calendar_connected", "Calendar connected"),
      });
      fetchAccounts();
      params.delete("calendarSync");
      params.delete("message");
      const next = `${window.location.pathname}${params.toString() ? `?${params}` : ""}`;
      window.history.replaceState({}, "", next);
    } else if (params.get("calendarSync") === "error") {
      $crud.notify({
        type: "error",
        message: params.get("message") || t("calendar_connect_failed", "Calendar connection failed"),
      });
      params.delete("calendarSync");
      params.delete("message");
      const next = `${window.location.pathname}${params.toString() ? `?${params}` : ""}`;
      window.history.replaceState({}, "", next);
    }
  }, [fetchAccounts]);

  // Web OAuth popup → backend HTML page posts this, then auto-closes.
  useEffect(() => {
    const onMessage = async (event) => {
      const data = event?.data;
      if (!data || data.type !== "kjuup-calendar-oauth") return;
      if (oauthTimerRef.current) {
        clearInterval(oauthTimerRef.current);
        oauthTimerRef.current = null;
      }
      setConnecting(null);
      if (data.success) {
        await $crud.notify({
          type: "success",
          message: data.message || t("calendar_connected", "Calendar connected"),
        });
        await fetchAccounts();
        dispatch({ type: REFRESH_TODO_LIST });
      } else {
        await $crud.notify({
          type: "error",
          message: data.message || t("calendar_connect_failed", "Calendar connection failed"),
        });
      }
    };
    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
      if (oauthTimerRef.current) clearInterval(oauthTimerRef.current);
    };
  }, [dispatch, fetchAccounts]);

  const connectOAuth = async (provider) => {
    if (locked) return;
    setConnecting(provider);
    try {
      const authUrl = await getCalendarOAuthUrl(provider);
      if (!authUrl) {
        setConnecting(null);
        await $crud.notify({
          type: "error",
          message: t("calendar_auth_url_failed", "Failed to get authorization URL."),
        });
        return;
      }
      const popup = window.open(
        authUrl,
        "kjuup-calendar-oauth",
        "width=600,height=700,scrollbars=yes"
      );
      if (!popup) {
        setConnecting(null);
        await $crud.notify({ type: "error", message: "Popup blocked. Please allow popups." });
        return;
      }
      if (oauthTimerRef.current) clearInterval(oauthTimerRef.current);
      oauthTimerRef.current = setInterval(() => {
        if (popup.closed) {
          clearInterval(oauthTimerRef.current);
          oauthTimerRef.current = null;
          setConnecting(null);
          fetchAccounts();
        }
      }, 500);
    } catch (e) {
      setConnecting(null);
      console.error(e);
    }
  };

  const connectCalDav = async () => {
    if (locked) return;
    if (!calDavForm.username.trim() || !calDavForm.password) {
      await $crud.notify({
        type: "error",
        message: t("calendar_caldav_required", "Username and password are required."),
      });
      return;
    }
    if (calDavForm.provider === "caldav" && !calDavForm.serverUrl.trim()) {
      await $crud.notify({
        type: "error",
        message: t("calendar_caldav_server_required", "Server URL is required for CalDAV."),
      });
      return;
    }
    setConnecting("caldav");
    try {
      const payload = {
        provider: calDavForm.provider,
        username: calDavForm.username.trim(),
        password: calDavForm.password,
      };
      if (calDavForm.provider === "caldav") payload.serverUrl = calDavForm.serverUrl.trim();
      const result = await connectCalDAVAccount(payload);
      if (!result.ok) {
        await $crud.notify({
          type: "error",
          message: result.message || t("calendar_connect_failed", "Connection failed"),
        });
      } else {
        await $crud.notify({
          type: "success",
          message: result.message || t("calendar_connected", "Calendar connected"),
        });
        setShowCalDav(false);
        setCalDavForm({ provider: "apple", username: "", password: "", serverUrl: "" });
        fetchAccounts();
      }
    } catch (e) {
      console.error(e);
      await $crud.notify({ type: "error", message: t("calendar_connect_failed", "Connection failed") });
    }
    setConnecting(null);
  };

  const patchAccount = async (id, patch) => {
    setBusyId(id);
    try {
      const result = await updateCalendarAccount(id, patch);
      if (!result.ok) {
        await $crud.notify({ type: "error", message: result.message });
      } else {
        setAccounts((prev) =>
          prev.map((a) => (a.id === id ? { ...a, ...patch, ...(result.data || {}) } : a))
        );
      }
    } catch (e) {
      console.error(e);
    }
    setBusyId(null);
  };

  const loadCalendars = async (id) => {
    try {
      const list = await listCalendarChoices(id);
      setCalendarsByAccount((prev) => ({ ...prev, [id]: list }));
    } catch (e) {
      console.error(e);
    }
  };

  const disconnect = async (id) => {
    try {
      document.body.setAttribute("data-confirm-color", "violet");
      await $crud.confirm({
        title: "",
        textContent: t(
          "calendar_disconnect_confirm",
          "Disconnect this calendar? Existing calendar events will be left as-is; sync will stop."
        ),
        options: { ok: t("ok", "OK"), cancel: t("cancel", "Cancel") },
      });
    } catch (_) {
      document.body.removeAttribute("data-confirm-color");
      return;
    }
    document.body.removeAttribute("data-confirm-color");
    setBusyId(id);
    try {
      const result = await disconnectCalendarAccount(id);
      if (result.ok) {
        setAccounts((prev) => prev.filter((a) => a.id !== id));
        await $crud.notify({
          type: "success",
          message: result.message || t("calendar_disconnected", "Calendar disconnected"),
        });
      } else {
        await $crud.notify({ type: "error", message: result.message });
      }
    } catch (e) {
      console.error(e);
    }
    setBusyId(null);
  };

  const syncNow = async () => {
    setSyncingNow(true);
    try {
      const result = await syncAllCalendarAccounts();
      if (!result.ok) {
        await $crud.notify({
          type: "error",
          message: result.message || t("calendar_sync_failed", "Sync failed"),
        });
      } else {
        const s = result.data || {};
        const parts = [];
        if (s.created) parts.push(`${s.created} new`);
        if (s.updated) parts.push(`${s.updated} updated`);
        if (s.deleted) parts.push(`${s.deleted} removed`);
        if (s.completed) parts.push(`${s.completed} completed`);
        await $crud.notify({
          type: "success",
          message: parts.length
            ? parts.join(" · ")
            : t("calendar_sync_up_to_date", "Calendar up to date"),
        });
        dispatch({ type: REFRESH_TODO_LIST });
      }
    } catch (e) {
      console.error(e);
      await $crud.notify({
        type: "error",
        message: t("calendar_sync_failed", "Sync failed"),
      });
    }
    setSyncingNow(false);
  };

  const providerLabel = (p) => {
    if (p === "google") return t("calendar_connect_google", "Google Calendar");
    if (p === "outlook") return t("calendar_connect_outlook", "Outlook Calendar");
    if (p === "apple") return "Apple";
    if (p === "caldav") return "CalDAV";
    return p;
  };

  const hasActiveAccounts = accounts.some((a) => a.syncEnabled && a.status === "active");
  const connectingLabel = t("email_connecting", "Connecting...");
  const completeOptions = [
    { value: "delete", label: t("calendar_on_complete_delete", "Remove calendar event") },
    { value: "mark", label: t("calendar_on_complete_mark", "Mark event as done") },
  ];
  const providerRows = [
    { key: "google", icon: "calendar_month", label: tr("pro_settings_cal_google", "Google Kalender", "Google Calendar") },
    { key: "outlook", icon: "calendar_view_week", label: tr("pro_settings_cal_outlook", "Outlook Kalender", "Outlook Calendar") },
  ];
  const calDavKinds = [
    { key: "apple", icon: "cloud_sync", label: "Apple (iCloud)" },
    { key: "caldav", icon: "dns", label: "Generic CalDAV" },
  ];

  // Kjuup.io paywall (mobile UpsellCard: the settings plan card look).
  const lockedNote = showCompanyTrialEndedNote
    ? companyTrialEndedAskBody(
        {
          kjuup_io_company_trial_ended_contact_admins: t(
            "kjuup_io_company_trial_ended_contact_admins",
            "The Kjuup.io subscription can be assigned by {admins} via the Admin Center."
          ),
          kjuup_io_company_trial_ended_body: t(
            "kjuup_io_company_trial_ended_body",
            "The Kjuup.io subscription can be assigned via the Admin Center."
          ),
        },
        subscription
      )
    : isKjuupPlusMember
      ? t("kjuup_io_company_managed_body_short", "Ask your company admin to assign you a Kjuup.io seat in the Admin Panel.")
      : hasUsedTrial
        ? t("kjuup_io_trial_ended_body", "Subscribe to Kjuup.io to keep using email and other premium features.")
        : null;

  return (
    <div className="calendar-sync-settings cal-pro">
      <SettingsNote className="cal-pro__intro">
        {t("calendar_sync_info", "Automatically sync all Kjuup ToDo's with your calendar.")}
      </SettingsNote>

      {locked ? (
        <div className="cal-pro__upsell">
          <div className="cal-pro__upsell-head">
            <span className="cal-pro__upsell-tile" aria-hidden="true">
              <span className="material-symbols-outlined">calendar_month</span>
            </span>
            <span className="cal-pro__upsell-text">
              <span className="cal-pro__upsell-title">{t("calendar_sync_title", "Calendar Sync")}</span>
              <span className="cal-pro__upsell-sub">{t("calendar_sync_info", "Automatically sync all Kjuup ToDo's with your calendar.")}</span>
            </span>
          </div>
          {lockedNote ? <p className="cal-pro__upsell-note">{lockedNote}</p> : null}
          {!isKjuupPlusMember ? (
            <button
              type="button"
              className="kj-btn kj-btn--primary kj-btn--lg kj-btn--block"
              onClick={() => navigateToKjuupIoBilling(history, subscription)}
            >
              <span className="material-symbols-outlined" aria-hidden="true">bolt</span>
              <span>
                {hasUsedTrial
                  ? t("kjuup_io_subscribe_to_continue", null) || t("kjuup_io_subscribe_now", "Subscribe now")
                  : t("kjuup_io_activate_to_sync_calendar", "Activate Kjuup.io to sync ToDos with my calendar")}
              </span>
            </button>
          ) : null}
        </div>
      ) : (
        <>
          <section className="kj-settings-section">
            <h3 className="kj-settings-caption">{tr("pro_settings_cal_connect", "Kalender verbinden", "Connect a calendar")}</h3>
            <div className="kj-settings-card">
              {providerRows.map((provider) => (
                <SettingsRow
                  key={provider.key}
                  icon={provider.icon}
                  label={connecting === provider.key ? connectingLabel : provider.label}
                  busy={connecting === provider.key}
                  dimmed={!!connecting && connecting !== provider.key}
                  onClick={connecting ? undefined : () => connectOAuth(provider.key)}
                />
              ))}
              <SettingsRow
                icon="cloud_sync"
                label={tr("pro_settings_cal_apple", "Apple / CalDAV", "Apple / CalDAV")}
                dimmed={!!connecting && connecting !== "caldav"}
                expanded={showCalDav}
                onClick={connecting && connecting !== "caldav" ? undefined : () => setShowCalDav((v) => !v)}
              >
                <form
                  className="cal-pro__caldav"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!connecting) connectCalDav();
                  }}
                >
                  <div className="cal-pro__segmented" role="tablist">
                    {calDavKinds.map((kind) => (
                      <button
                        key={kind.key}
                        type="button"
                        role="tab"
                        aria-selected={calDavForm.provider === kind.key}
                        className={`kj-tab cal-pro__segment${calDavForm.provider === kind.key ? " is-active" : ""}`}
                        onClick={() => setCalDavForm((f) => ({ ...f, provider: kind.key }))}
                      >
                        <span className="material-symbols-outlined" aria-hidden="true">{kind.icon}</span>
                        <span>{kind.label}</span>
                      </button>
                    ))}
                  </div>
                  {calDavForm.provider === "apple" ? (
                    <p className="cal-pro__hint">
                      {t("calendar_apple_hint", "Use your Apple ID email and an app-specific password from appleid.apple.com.")}
                    </p>
                  ) : null}
                  {calDavForm.provider === "caldav" ? (
                    <label className="cal-pro__field">
                      <span className="cal-pro__label">{t("calendar_server_url", "CalDAV server URL")}</span>
                      <span className="cal-pro__input">
                        <span className="material-symbols-outlined" aria-hidden="true">dns</span>
                        <input
                          type="url"
                          autoComplete="off"
                          value={calDavForm.serverUrl}
                          onChange={(e) => setCalDavForm((f) => ({ ...f, serverUrl: e.target.value }))}
                        />
                      </span>
                    </label>
                  ) : null}
                  <label className="cal-pro__field">
                    <span className="cal-pro__label">{t("calendar_username", "Username / email")}</span>
                    <span className="cal-pro__input">
                      <span className="material-symbols-outlined" aria-hidden="true">person</span>
                      <input
                        type="text"
                        autoComplete="username"
                        value={calDavForm.username}
                        onChange={(e) => setCalDavForm((f) => ({ ...f, username: e.target.value }))}
                      />
                    </span>
                  </label>
                  <label className="cal-pro__field">
                    <span className="cal-pro__label">{t("calendar_password", "Password / app-specific password")}</span>
                    <span className="cal-pro__input">
                      <span className="material-symbols-outlined" aria-hidden="true">lock</span>
                      <input
                        type="password"
                        autoComplete="current-password"
                        value={calDavForm.password}
                        onChange={(e) => setCalDavForm((f) => ({ ...f, password: e.target.value }))}
                      />
                    </span>
                  </label>
                  <button
                    type="submit"
                    className={`kj-btn kj-btn--primary kj-btn--block${connecting === "caldav" ? " kj-btn--loading" : ""}`}
                    disabled={!!connecting}
                  >
                    {connecting === "caldav" ? connectingLabel : t("calendar_connect", "Connect")}
                  </button>
                </form>
              </SettingsRow>
            </div>
          </section>

          {loading && !accounts.length ? (
            <div className="kj-settings-card cal-pro__skeleton" aria-busy="true">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="cal-pro__bone-row">
                  <span className="cal-pro__bone cal-pro__bone--tile" />
                  <span className={`cal-pro__bone cal-pro__bone--line${i % 2 ? " is-short" : ""}`} />
                </div>
              ))}
            </div>
          ) : accounts.length ? (
            <section className="kj-settings-section">
              <div className="cal-pro__caption-row">
                <h3 className="kj-settings-caption">{tr("v2_settings_connected", "Verbunden", "Connected")}</h3>
                <button
                  type="button"
                  className="kj-btn kj-btn--ghost kj-btn--sm"
                  disabled={syncingNow || !!connecting || !hasActiveAccounts}
                  onClick={syncNow}
                >
                  <span className="material-symbols-outlined" aria-hidden="true">sync</span>
                  <span>{syncingNow ? t("calendar_syncing", "Updating from calendar…") : t("calendar_sync_now", "Sync now")}</span>
                </button>
              </div>
              {accounts.map((account) => {
                const id = account.id;
                const cals = calendarsByAccount[id] || [];
                const authError = account.status === "auth_error";
                const canReconnect = authError && (account.provider === "google" || account.provider === "outlook");
                const current = cals.find((c) => c.id === account.defaultCalendarId);
                // Mobile: the calendar's name, else its id, else "primary".
                const targetLabel = current ? current.summary : account.defaultCalendarId || "primary";
                const targetValue = account.defaultCalendarId || "primary";
                const targetOptions = cals.length
                  ? cals.map((c) => ({ value: c.id, label: `${c.summary}${c.primary ? " (primary)" : ""}` }))
                  : [];
                if (!targetOptions.some((o) => o.value === targetValue)) {
                  targetOptions.unshift({ value: targetValue, label: targetLabel });
                }
                const completeValue = account.onCompleteAction || "delete";
                const busy = busyId === id;
                return (
                  <div key={id} className="kj-settings-card cal-pro__account">
                    <SettingsRow
                      icon="calendar_month"
                      label={account.email}
                      sub={providerLabel(account.provider)}
                      trailing={authError ? <SettingsTag tone="warn">{t("calendar_auth_error", "Reconnect required")}</SettingsTag> : null}
                    />
                    {canReconnect ? (
                      <SettingsRow
                        icon="sync_problem"
                        label={t("calendar_reconnect", "Reconnect")}
                        busy={connecting === account.provider}
                        onClick={connecting || busy ? undefined : () => connectOAuth(account.provider)}
                      />
                    ) : null}
                    <SettingsRow
                      icon="sync"
                      label={t("calendar_sync_enabled", "Sync enabled")}
                      toggle={{
                        checked: !!account.syncEnabled,
                        onChange: () => patchAccount(id, { syncEnabled: !account.syncEnabled }),
                        disabled: busy,
                      }}
                    />
                    <SelectRow
                      icon="task_alt"
                      label={t("calendar_on_complete", "When ToDo is completed")}
                      value={completeValue}
                      valueLabel={(completeOptions.find((o) => o.value === completeValue) || completeOptions[0]).label}
                      options={completeOptions}
                      disabled={busy}
                      onChange={(value) => patchAccount(id, { onCompleteAction: value })}
                    />
                    <SelectRow
                      icon="event_note"
                      label={t("calendar_target", "Target calendar")}
                      value={targetValue}
                      valueLabel={targetLabel}
                      options={targetOptions}
                      disabled={busy}
                      onFocus={() => {
                        if (!cals.length) loadCalendars(id);
                      }}
                      onChange={(value) => patchAccount(id, { defaultCalendarId: value })}
                    />
                    <SettingsRow
                      icon="link_off"
                      danger
                      label={t("email_disconnect", "Disconnect")}
                      busy={busy}
                      onClick={busy ? undefined : () => disconnect(id)}
                    />
                  </div>
                );
              })}
            </section>
          ) : (
            <div className="cal-pro__empty">
              <span className="material-symbols-outlined" aria-hidden="true">event_busy</span>
              <span>{t("calendar_no_accounts", "No calendar connected yet.")}</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}
